import React, { useEffect, useState } from 'react';
import { XIcon, BuildingIcon, ChevronDownIcon, UploadIcon, Trash2Icon } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
interface EditCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  readOnly?: boolean;
}
type TabType = 'legal' | 'address' | 'contact';
export function EditCompanyModal({
  isOpen,
  onClose,
  readOnly = false
}: EditCompanyModalProps) {
  const {
    role,
    companyName,
    companyLogo,
    setCompanyLogo,
    officialCountry,
    setOfficialCountry
  } = useUser();
  const [activeTab, setActiveTab] = useState<TabType>('legal');
  const [logoPreview, setLogoPreview] = useState<string | null>(companyLogo);
  // Legal data
  const [legalName, setLegalName] = useState('DAIRY GLOBAL EXPERTS LLC LN');
  const [taxId, setTaxId] = useState('INN 392949923233');
  const [billingCountry, setBillingCountry] = useState('');
  // Billing address
  const [lineAddress1, setLineAddress1] = useState('50 Centralna st');
  const [lineAddress2, setLineAddress2] = useState('');
  const [city, setCity] = useState('Hromy village, Uman district');
  const [stateProvince, setStateProvince] = useState('Cherkasy Region');
  const [zipCode, setZipCode] = useState('20343');
  const [distributionCountry, setDistributionCountry] = useState('Ukraine');
  // Contact
  const [phone, setPhone] = useState('+380 97 410 2423');
  const [email, setEmail] = useState('irrigation.dge@gmail.com');
  const [url, setUrl] = useState('www.dge.com.ua');
  const [dealerMarkup, setDealerMarkup] = useState('25.00');
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('legal');
      setLegalName('DAIRY GLOBAL EXPERTS LLC LN');
      setTaxId('INN 392949923233');
      setBillingCountry('');
      setLineAddress1('50 Centralna st');
      setLineAddress2('');
      setCity('Hromy village, Uman district');
      setStateProvince('Cherkasy Region');
      setZipCode('20343');
      setDistributionCountry('Ukraine');
      setPhone('+380 97 410 2423');
      setEmail('irrigation.dge@gmail.com');
      setUrl('www.dge.com.ua');
      setDealerMarkup('25.00');
      setLogoPreview(companyLogo);
    }
  }, [isOpen, companyLogo]);
  // Determine if user can edit logo and official country
  const canEditLogo = ['sales-manager', 'operations-manager', 'manager-business-unit', 'ceo', 'logistic', 'marketing', 'financial-manager', 'system-admin', 'dealer-admin', 'region-admin'].includes(role);
  const canEditOfficialCountry = canEditLogo;
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleRemoveLogo = () => {
    setLogoPreview(null);
  };
  const handleSave = () => {
    setCompanyLogo(logoPreview);
    onClose();
  };
  if (!isOpen) return null;
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  const countries = ['México', 'Estados Unidos', 'España', 'Colombia', 'Argentina', 'Brasil', 'Chile', 'Perú', 'Alemania', 'Francia', 'Ucrania', 'Ukraine', 'Reino Unido', 'Italia', 'Canadá'];
  const tabs = [{
    id: 'legal' as TabType,
    label: 'Legal Data'
  }, {
    id: 'address' as TabType,
    label: 'Address'
  }, {
    id: 'contact' as TabType,
    label: 'Contact'
  }];
  return <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={handleBackdropClick} data-id="element-3215" data-mp="f5nelc39pxzn">
      <div className="bg-[#0f1d3a] rounded-2xl shadow-2xl border border-white/10 w-full max-w-2xl my-8 relative flex flex-col max-h-[90vh]" data-id="element-3216" data-mp="o4hg9gvghek2">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0" data-id="element-3217" data-mp="1zje8ezmxsjvn">
          <h2 className="text-white font-semibold text-lg" data-id="element-3218" data-mp="1y1z3ry61ui7t">
            {readOnly ? 'Company Information' : 'Edit Company Information'}
          </h2>
          <button onClick={onClose} className="text-blue-300 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10" data-id="element-3219" data-mp="1t4p840s2habj">
            <XIcon size={20} data-id="element-3220" data-mp="231bqmiwx7pae" />
          </button>
        </div>

        {/* Company Header Section */}
        <div className="px-6 py-4 flex-shrink-0 border-b border-white/10" data-id="element-3221" data-mp="2h13cf5toi9yy">
          <div className="flex items-center justify-between gap-4" data-id="element-3222" data-mp="2gz0bvruwgyfz">
            {/* Left side: Logo and Company Name */}
            <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-3223" data-mp="2qqwm21a9gf0k">
              {/* Logo Section */}
              <div className="relative flex-shrink-0" data-id="element-3224" data-mp="2lrtk4ppkuowt">
                {logoPreview ? <div className="w-14 h-14 rounded-lg overflow-hidden bg-white/5 border border-white/10" data-id="element-3225" data-mp="2ltwh5tp5dib1">
                    <img src={logoPreview} alt="Company logo" className="w-full h-full object-cover" data-id="element-3226" data-mp="2zp8abwnnn0pu" />
                  </div> : <div className="w-14 h-14 rounded-lg bg-blue-600/20 flex items-center justify-center border border-white/10" data-id="element-3227" data-mp="3ee9zyyxtiukd">
                    <BuildingIcon size={28} className="text-blue-400" data-id="element-3228" data-mp="3jdmlngcydvld" />
                  </div>}

                {/* Logo Upload/Remove Controls */}
                {canEditLogo && !readOnly && <div className="absolute -bottom-1 -right-1 flex gap-1" data-id="element-3229" data-mp="3acoi0z6ffq3b">
                    <label className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white rounded-full p-1 shadow-lg border-2 border-[#0f1d3a] transition-colors" data-id="element-3230" data-mp="3aesqzvj6nfag">
                      <UploadIcon size={10} data-id="element-3231" data-mp="30fa9dlqqw5lk" />
                      <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-id="element-3232" data-mp="2mn3v6575k8or" />
                    </label>
                    {logoPreview && <button onClick={handleRemoveLogo} className="bg-red-600 hover:bg-red-700 text-white rounded-full p-1 shadow-lg border-2 border-[#0f1d3a] transition-colors" data-id="element-3233" data-mp="2qpiculz352eg">
                        <Trash2Icon size={10} data-id="element-3234" data-mp="2hoahabnyefo2" />
                      </button>}
                  </div>}
              </div>

              {/* Company Name */}
              <div className="flex-1 min-w-0" data-id="element-3235" data-mp="n9wt95g82cvq">
                <h3 className="text-lg font-semibold text-white truncate" data-id="element-3236" data-mp="igu3l8ekfnkt">
                  {companyName}
                </h3>
              </div>
            </div>

            {/* Right side: Distribution Country */}
            <div className="flex-shrink-0" data-id="element-3237" data-mp="9hozzno5d41s">
              <p className="text-xs text-blue-300 mb-1" data-id="element-3238" data-mp="166oazag48jxo">
                Distribuidor oficial en
              </p>
              {canEditOfficialCountry && !readOnly ? <div className="relative" data-id="element-3239" data-mp="126e47vki1phr">
                  <select value={distributionCountry} onChange={e => setDistributionCountry(e.target.value)} className="appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-8 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-3240" data-mp="x5zxqhyjqnlu">
                    {countries.map(c => <option key={c} value={c} className="bg-slate-800 text-white" data-id="element-3241" data-mp="3cwuszqtgkgtd">
                        {c}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-blue-300" data-id="element-3242" data-mp="37sa9caifpmuk">
                    <ChevronDownIcon size={14} data-id="element-3243" data-mp="3cqtvaax12ymx" />
                  </div>
                </div> : <p className="text-sm font-medium text-white" data-id="element-3244" data-mp="3hi3jr86ubvx8">
                  {distributionCountry || 'No country set'}
                </p>}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 px-6 flex-shrink-0" data-id="element-3245" data-mp="3qii7sfb6vp4f">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-3 text-sm font-medium transition-colors relative ${activeTab === tab.id ? 'text-white' : 'text-blue-300 hover:text-white'}`} data-id="element-3246" data-mp="3vi4jgkbdcxb4">
              {tab.label}
              {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" data-id="element-3247" data-mp="215wuropn5cns" />}
            </button>)}
        </div>

        {/* Content (Scrollable) */}
        <div className="p-6 overflow-y-auto flex-1 no-scrollbar" data-id="element-3248" data-mp="bub21n7y8rqh">
          {/* Legal Data Tab */}
          {activeTab === 'legal' && <div className="space-y-5" data-id="element-3249" data-mp="6vgkeolpx97y">
              <div data-id="element-3250" data-mp="lkw81vpycyjf">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3251" data-mp="lle68xw6dmtz">
                  Legal name <span className="text-red-400" data-id="element-3252" data-mp="gl9m8xw269z0">*</span>
                </label>
                <input type="text" value={legalName} onChange={e => setLegalName(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="Company legal name" data-id="element-3253" data-mp="3p1l7yyv15a04" />
              </div>

              <div data-id="element-3254" data-mp="1db0stvhmjnl6">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3255" data-mp="1dahaido2ax6m">
                  TAX ID / VAT ID / NIF <span className="text-red-400" data-id="element-3256" data-mp="19a75b383gast">*</span>
                </label>
                <input type="text" value={taxId} onChange={e => setTaxId(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="Tax identification number" data-id="element-3257" data-mp="1gt6n54fwpua2" />
              </div>

              <div data-id="element-3258" data-mp="1zmtrpmkcxv97">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3259" data-mp="24k3mlhqimz4f">
                  Billing country <span className="text-red-400" data-id="element-3260" data-mp="1pv7hntjra281">*</span>
                </label>
                <div className="relative" data-id="element-3261" data-mp="1utr5jzhwto31">
                  <select value={billingCountry} onChange={e => setBillingCountry(e.target.value)} disabled={readOnly} className={`w-full appearance-none border rounded-lg px-4 py-2.5 text-white focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-3262" data-mp="1uspiewnmdb4y">
                    <option value="" className="bg-slate-800 text-blue-300" data-id="element-3263" data-mp="1q007e98y44y4">
                      Please, select country
                    </option>
                    {countries.map(c => <option key={c} value={c} className="bg-slate-800 text-white" data-id="element-3264" data-mp="1gytqhz2dws4c">
                        {c}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-3265" data-mp="128mhe9y9olgb">
                    <ChevronDownIcon size={16} data-id="element-3266" data-mp="172h0bb61swec" />
                  </div>
                </div>
                {!billingCountry && <p className="text-red-400 text-xs mt-1" data-id="element-3267" data-mp="y8c40c85asrf">
                    Please, select country
                  </p>}
              </div>
            </div>}

          {/* Billing Address Tab */}
          {activeTab === 'address' && <div className="space-y-5" data-id="element-3268" data-mp="oaefroqacvyt">
              <div data-id="element-3269" data-mp="o9d016ybrlar">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3270" data-mp="obzc9jw5uozb">
                  Address line 1 <span className="text-red-400" data-id="element-3271" data-mp="4q60gijotxyd">*</span>
                </label>
                <input type="text" value={lineAddress1} onChange={e => setLineAddress1(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="Street, number" data-id="element-3272" data-mp="oua2aszp8p4" />
              </div>

              <div data-id="element-3273" data-mp="xdjqdwoyxt8z">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3274" data-mp="xbyxscm7skdr">
                  Address line 2
                </label>
                <input type="text" value={lineAddress2} onChange={e => setLineAddress2(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="Apartment, suite, etc. (optional)" data-id="element-3275" data-mp="sg0cwz03hki3" />
              </div>

              <div data-id="element-3276" data-mp="1jcpg3tvey5lf">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3277" data-mp="1jc68p73e2zln">
                  City <span className="text-red-400" data-id="element-3278" data-mp="1o5sd6bzefx6d">*</span>
                </label>
                <input type="text" value={city} onChange={e => setCity(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="City" data-id="element-3279" data-mp="1t290x52os9ix" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-3280" data-mp="7rio1fnc24eh">
                <div data-id="element-3281" data-mp="co93q1v86xzd">
                  <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3282" data-mp="cptynslgcixr">
                    State / Province
                  </label>
                  <input type="text" value={stateProvince} onChange={e => setStateProvince(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="State or province" data-id="element-3283" data-mp="et82ffojqv8" />
                </div>

                <div data-id="element-3284" data-mp="j6lx5yrmorxo">
                  <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3285" data-mp="j37wqw2iyssv">
                    Zip code
                  </label>
                  <input type="text" value={zipCode} onChange={e => setZipCode(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="Zip code" data-id="element-3286" data-mp="97mqumuc58du" />
                </div>
              </div>

              <div data-id="element-3287" data-mp="re6ogzx8fpdh">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3288" data-mp="reougo9p5hyl">
                  Distribution country <span className="text-red-400" data-id="element-3289" data-mp="hn28bwvtlyh2">*</span>
                </label>
                <div className="relative" data-id="element-3290" data-mp="hky7xox4rhlj">
                  <select value={distributionCountry} onChange={e => setDistributionCountry(e.target.value)} disabled={readOnly} className={`w-full appearance-none border rounded-lg px-4 py-2.5 text-white focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-3291" data-mp="15c3ippo7rjoz">
                    {countries.map(c => <option key={c} value={c} className="bg-slate-800 text-white" data-id="element-3292" data-mp="3g3myrtyeqfh1">
                        {c}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-3293" data-mp="235w5tddgmysg">
                    <ChevronDownIcon size={16} data-id="element-3294" data-mp="1y7umqsg1azgz" />
                  </div>
                </div>
              </div>
            </div>}

          {/* Contact Tab */}
          {activeTab === 'contact' && <div className="space-y-5" data-id="element-3295" data-mp="284pmfeo1n5wu">
              <div data-id="element-3296" data-mp="285qvxepevkcg">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3297" data-mp="2834odf2it4u8">
                  Phone
                </label>
                <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="+1 234 567 8900" data-id="element-3298" data-mp="1fgtok6mqn7yh" />
              </div>

              <div data-id="element-3299" data-mp="wp0v6b3rbkq8">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3300" data-mp="wptwgwjy5i0z">
                  Email
                </label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="company@email.com" data-id="element-3301" data-mp="30tx6cq996kj1" />
              </div>

              <div data-id="element-3302" data-mp="2hu9hq7fjaegi">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3303" data-mp="2hvudg0dl43fy">
                  Website
                </label>
                <input type="url" value={url} onChange={e => setUrl(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="www.company.com" data-id="element-3304" data-mp="2mwicn9byvql9" />
              </div>

              <div data-id="element-3305" data-mp="1mm7f4phsim0j">
                <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-3306" data-mp="1mn0gfaxzcjba">
                  Dealer markup <span className="text-red-400" data-id="element-3307" data-mp="1huh6qp2ey8br">*</span>
                </label>
                <div className="relative" data-id="element-3308" data-mp="1cwnylqhjv48u">
                  <input type="number" step="0.01" value={dealerMarkup} onChange={e => setDealerMarkup(e.target.value)} readOnly={readOnly} className={`w-full border rounded-lg px-4 py-2.5 pr-12 text-white placeholder-blue-300/50 focus:outline-none transition-all ${readOnly ? 'bg-white/5 border-white/5 cursor-not-allowed' : 'bg-white/10 border-white/10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} placeholder="25.00" data-id="element-3309" data-mp="1cvmllggz47oh" />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-blue-300" data-id="element-3310" data-mp="2tae3gzghtwjj">
                    %
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end space-x-3 flex-shrink-0 bg-black/20 rounded-b-2xl" data-id="element-3311" data-mp="1x6h1n8j4uw3u">
          <button onClick={onClose} className="bg-white/10 hover:bg-white/15 text-blue-200 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors" data-id="element-3312" data-mp="1ng5jagypckdf">
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && <button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-3313" data-mp="1ef7nun2bxl6g">
              Save changes
            </button>}
        </div>
      </div>
    </div>;
}